/**
 * Google Drive API Service - PROJECTS MODULE
 *
 * Handles all project-related operations:
 * - Project CRUD (create, read, update, delete)
 * - Project metadata (onboarding, outline, orientation)
 * - Project references management
 * - Project research data management
 * - Project crossings (cruzamentos)
 * - Project groups (data grouping)
 *
 * Inherits from GoogleDriveServiceCore which provides:
 * - getTokenOrRefresh(), readProjectJson(), saveProjectJson()
 * - findOrCreateSubfolder(), createFolder(), appFolderId
 */

import { GoogleDriveServiceCore, DRIVE_API_BASE } from './core';
import { driveFetch } from './driveFetch';
import type { OrientationData } from '../../types/orientation';
import type { ProjectReference, ProjectResearchData, DataGroup, ProjectCrossing } from '../../stores/driveProject/types';
import type { EmpiricalCodebook, EmpiricalCategory } from '../../types/empirical';
import { assignNextCategoryColor } from '../../constants/empirical-colors';
import { logger } from '../../utils/logger';
import { auditLog } from '../../utils/auditLog';
import { CURRENT_SCHEMA_VERSION } from './schemaMigration';

// ============================================
// PROJECT ID — BRANDED TYPE (Sprint 014, ADR-0001)
// ============================================
//
// O id canônico da Mesa É o Drive folder ID (decisão D1, 05/jun/2026 —
// docs/canon/adr/ADR-0001-mesa-id-drive-folder-id.md). O brand impede
// fabricar um ProjectId a partir de string arbitrária quando uma assinatura
// exigir ProjectId (ex.: Sprint B, `originProjectId: ProjectId`). Fora de
// services/googleDrive/, o valor é OPACO: proibido parse/split/regex/
// dependência de formato — usos legítimos são igualdade, chave de mapa e
// URL param via encodeURIComponent (regra de opacidade do ADR-0001).
export type ProjectId = string & { readonly __brand: 'ProjectId' };

// ============================================
// FASE EMPÍRICA v4 — MIGRATION HELPERS (DN8, DN9)
// ============================================
//
// Pure functions que transformam dados do codebook empírico — sem efeitos
// colaterais, totalmente testáveis em isolamento. A orquestração async
// (ler project.json + ler cada SavedPdfData + salvar) acontece em
// `migrateProjectV5toV6Codebook` abaixo, ou pode ser feita pelo caller.
//
// Pilar 1: estas funções NÃO destroem dados — sempre acrescentam categorias,
// nunca removem; sempre preservam IDs existentes; idempotência garantida.

/**
 * Coleta labels únicos de um array de highlights, varrendo `empiricalTags`.
 *
 * Entrada: highlights tal como vêm de SavedPdfData (legacy v5: empiricalTags
 * pode conter labels-string OU IDs-string — esta função retorna ambos sem
 * distinguir). Filtra strings vazias e duplicatas.
 *
 * Pure, sync, idempotente.
 */
export function collectLabelsFromHighlights(
  highlights: ReadonlyArray<{ empiricalTags?: ReadonlyArray<string> | undefined }>,
): string[] {
  const seen = new Set<string>();
  for (const h of highlights) {
    const tags = h.empiricalTags ?? [];
    for (const tag of tags) {
      if (typeof tag === 'string' && tag.length > 0) {
        seen.add(tag);
      }
    }
  }
  return Array.from(seen);
}

/**
 * Gera ID estável para categoria do codebook empírico.
 *
 * Padrão: `cat_<12-char-uuid>`. Usa crypto.randomUUID quando disponível;
 * fallback para Math.random + timestamp em ambientes sem WebCrypto (jsdom
 * pre-2023). Não criptograficamente forte — apenas estável e único o
 * suficiente para escopo de Mesa.
 */
export function generateCategoryId(): string {
  const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `cat_${uuid.replace(/-/g, '').slice(0, 12)}`;
}

/**
 * Gera ID estável para coluna do codebook empírico.
 *
 * Padrão: `col_<12-char-uuid>`. Mesma estratégia de `generateCategoryId` —
 * prefixo diferente para rastreabilidade em logs (Sprint 0.2 da Fase Empírica v4).
 *
 * @see generateCategoryId
 */
export function generateColumnId(): string {
  const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `col_${uuid.replace(/-/g, '').slice(0, 12)}`;
}

/**
 * Constrói novas EmpiricalCategory entries a partir de labels, atribuindo
 * cores via DN30 (paleta de 8 cores cycling por `length % 8`).
 *
 * `existingCount` é o número de categorias JÁ existentes no codebook (antes
 * destes labels serem adicionados) — usado para o offset do ciclo de cores.
 * Pure, sync, deterministic exceto pelo IDs (que vêm de generateCategoryId).
 *
 * @example
 * buildCategoriesFromLabels(['ratio', 'obiter'], 0)
 *   → [{ id: 'cat_...', label: 'ratio', color: '#477B72' },
 *      { id: 'cat_...', label: 'obiter', color: '#F2BC41' }]
 */
export function buildCategoriesFromLabels(
  labels: ReadonlyArray<string>,
  existingCount: number,
): EmpiricalCategory[] {
  return labels.map((label, index) => ({
    id: generateCategoryId(),
    label,
    color: assignNextCategoryColor(existingCount + index),
  }));
}

/**
 * Funde labels novos num codebook existente, preservando categorias antigas.
 *
 * Idempotente (DN13 + Pilar 1):
 * - Labels já presentes em `codebook.categories[].label` são ignorados
 * - Labels novos viram categorias com IDs novos e cores via DN30
 * - Ordem das categorias existentes é preservada; novas são acrescidas no fim
 * - Colunas do codebook NÃO são tocadas (eixo independente — DN5/DN7)
 *
 * Pure, sync, retorna NOVO objeto (sem mutar input).
 */
export function mergeCodebookWithLabels(
  codebook: EmpiricalCodebook,
  labels: ReadonlyArray<string>,
): EmpiricalCodebook {
  const existingLabels = new Set(codebook.categories.map((c) => c.label));
  const newLabels = labels.filter((l) => !existingLabels.has(l));
  if (newLabels.length === 0) return codebook; // idempotente — nada a fazer
  const newCategories = buildCategoriesFromLabels(newLabels, codebook.categories.length);
  return {
    columns: codebook.columns,
    categories: [...codebook.categories, ...newCategories],
  };
}

/**
 * Tipo do callback de leitura de highlights — abstrai a dependência de
 * `loadHighlights` que vive na subclasse `GoogleDriveServiceRepository`.
 * Permite que o orquestrador rode em isolamento (testes) ou integrado.
 */
export type LoadHighlightsCallback = (
  workId: string,
) => Promise<{ highlights?: ReadonlyArray<{ empiricalTags?: ReadonlyArray<string> | undefined }> } | null>;



export class GoogleDriveServiceProjects extends GoogleDriveServiceCore {

  // Serializa escritas no project.json por projectId para evitar race conditions
  // (read-modify-write concurrent perde campos como isFlagged/isFavorite)
  private _projectWriteLocks = new Map<string, Promise<void>>();

  private async withProjectLock<T>(projectId: string, fn: () => Promise<T>): Promise<T> {
    const prev = this._projectWriteLocks.get(projectId) ?? Promise.resolve();
    const next = prev.then(fn, fn);
    this._projectWriteLocks.set(projectId, next.then(() => {}, () => {}));
    return next;
  }

  // ============================================
  // PROJECT CREATION
  // ============================================

  /**
   * Create a complete project with folder structure and metadata
   * @param name - Project name
   * @param description - Project description
   * @returns Promise<ProjectId> - Project folder ID (id canônico da Mesa — ADR-0001)
   */
  async createProject(name: string, description: string): Promise<ProjectId> {
    try {
      // 1. Create project folder
      const projectFolderId = await this.createProjectFolder(name);

      // 2. Create project.json with metadata
      const projectData = {
        name,
        description,
        createdAt: new Date().toISOString(),
        status: 'active',
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };
      await this.createProjectJson(projectFolderId, projectData);

      auditLog({ operation: 'create', target: 'projeto', targetId: projectFolderId, detail: `Projeto criado: ${name}`, success: true });
      // Fronteira do brand (ADR-0001): o Drive folder ID nasce aqui como id
      // canônico da Mesa. Único ponto do repo autorizado a fabricar ProjectId.
      // createProjectFolder permanece Promise<string> — devolve um folder ID;
      // quem sabe que essa pasta É uma Mesa é este método.
      return projectFolderId as ProjectId;
    } catch (error) {
      auditLog({ operation: 'create', target: 'projeto', detail: `Falha ao criar projeto: ${name}`, success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Create project folder inside Bigwa_App/Projetos/
   * Returns the folder ID
   */
  async createProjectFolder(projectName: string): Promise<string> {
    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Ensure Projetos folder exists
    const projetosId = await this.findOrCreateSubfolder('Projetos');

    // Create project folder inside Projetos/
    const projectFolderId = await this.createFolder(projetosId, projectName);

    // Create subfolders inside project folder
    await this.createProjectSubfolders(projectFolderId);

    return projectFolderId;
  }

  /**
   * Create project.json file inside project folder
   * Returns the file ID
   */
  async createProjectJson(projectFolderId: string, projectData: Record<string, unknown>): Promise<string> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const metadata = {
      name: 'project.json',
      mimeType: 'application/json',
      parents: [projectFolderId]
    };

    const file = new Blob([JSON.stringify(projectData, null, 2)], { type: 'application/json' });
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
    const response = await driveFetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: form
    });

    if (!response.ok) {
      throw new Error(`Failed to create project.json: ${response.statusText}`);
    }

    const data = await response.json();
    return data.id;
  }

  /**
   * Create standard subfolders inside a project folder
   * Creates: Referencias/, Dados/, Notas/
   */
  async createProjectSubfolders(projectFolderId: string): Promise<void> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const subfolders = ['Referencias', 'Dados', 'Notas'];

    for (const folderName of subfolders) {
      await this.createFolder(projectFolderId, folderName);
    }

  }

  // ============================================
  // PROJECT LISTING
  // ============================================

  /**
   * List all projects from Bigwa_App/Projetos/ folder
   * Returns array of project folders with their metadata
   */
  async listProjects(): Promise<Array<{ id: ProjectId; name: string; createdTime: string }>> {
    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Get Projetos folder ID
    const projetosId = await this.findOrCreateSubfolder('Projetos');

    // List all folders inside Projetos/ (incluindo lixeira para exibir projetos deletados)
    const query = `'${projetosId}' in parents and mimeType='application/vnd.google-apps.folder'`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,name,createdTime,trashed)&orderBy=createdTime desc`;

    const response = await driveFetch(searchUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to list projects: ${response.statusText}`);
    }

    const data = await response.json();
    return data.files || [];
  }

  // ============================================
  // PROJECT ARCHIVE / DELETE
  // ============================================

  /**
   * Archive a project by renaming its folder to [ARQUIVADO] Name
   * @param folderId - The Google Drive folder ID of the project
   * @param currentName - Current name of the project
   * @returns Promise<void>
   */
  async archiveProject(folderId: string, currentName: string): Promise<void> {
    try {
      const accessToken = await this.getTokenOrRefresh();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Check if already archived
      if (currentName.startsWith('[ARQUIVADO] ')) {
        return;
      }

      const newName = `[ARQUIVADO] ${currentName}`;
      const updateUrl = `${DRIVE_API_BASE}/files/${folderId}`;

      const response = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to archive project: ${error.error?.message || response.statusText}`);
      }

      auditLog({ operation: 'update', target: 'projeto', targetId: folderId, detail: `Projeto arquivado: ${currentName}`, success: true });
    } catch (error) {
      auditLog({ operation: 'update', target: 'projeto', targetId: folderId, detail: `Falha ao arquivar projeto: ${currentName}`, success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Delete a project by moving its folder to Google Drive trash
   * @param folderId - The Google Drive folder ID of the project
   * @returns Promise<void>
   */
  async deleteProject(folderId: string): Promise<void> {
    try {
      const accessToken = await this.getTokenOrRefresh();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // 1. Salvar trashedAt no project.json antes de mover para lixeira
      try {
        await this.updateProjectJson(folderId, {
          trashedAt: new Date().toISOString(),
        });
      } catch (error) {
        logger.warn('GoogleDriveProjects', 'Falha ao salvar trashedAt no project.json:', error);
      }

      // 2. Mover pasta para lixeira do Google Drive
      const updateUrl = `${DRIVE_API_BASE}/files/${folderId}`;

      const response = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ trashed: true })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to delete project: ${error.error?.message || response.statusText}`);
      }

      auditLog({ operation: 'delete', target: 'projeto', targetId: folderId, detail: 'Projeto movido para lixeira', success: true });
    } catch (error) {
      auditLog({ operation: 'delete', target: 'projeto', targetId: folderId, detail: 'Falha ao deletar projeto', success: false, error: String(error) });
      throw error;
    }
  }

  // ============================================
  // PROJECT UPDATE / METADATA
  // ============================================

  /**
   * Atualiza metadados do projeto (nome, descrição)
   */
  async updateProject(
    projectId: string,
    updates: { name?: string; description?: string; template?: string; visiblePages?: Record<string, boolean>; pageLabels?: Record<string, string>; sidebarDividers?: boolean | null }
  ): Promise<void> {
    try {
      const token = await this.getTokenOrRefresh();
      if (!token) {
        throw new Error('Não autenticado. Faça login primeiro.');
      }

      // 1. Atualizar nome da pasta (se fornecido)
      if (updates.name) {
        const response = await driveFetch(
          `https://www.googleapis.com/drive/v3/files/${projectId}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: updates.name,
            }),
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(`Erro ao atualizar nome: ${error.error.message}`);
        }
      }

      // 2. Atualizar project.json (se há campos para persistir)
      const jsonUpdates: Record<string, unknown> = {};
      if (updates.description !== undefined) jsonUpdates.description = updates.description;
      if (updates.template !== undefined) jsonUpdates.template = updates.template;
      if (updates.visiblePages !== undefined) jsonUpdates.visiblePages = updates.visiblePages;
      if (updates.pageLabels !== undefined) jsonUpdates.pageLabels = updates.pageLabels;
      if (updates.sidebarDividers !== undefined) jsonUpdates.sidebarDividers = updates.sidebarDividers;

      if (Object.keys(jsonUpdates).length > 0) {
        await this.updateProjectJson(projectId, jsonUpdates);
      }

      auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: `Projeto atualizado (keys: ${Object.keys(updates).join(', ')})`, success: true });
    } catch (error) {
      auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: 'Falha ao atualizar projeto', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Atualiza metadados no project.json
   */
  async updateProjectJson(
    projectId: string,
    updates: Partial<{
      name: string;
      description: string;
      createdAt: string;
      updatedAt: string;
      trashedAt: string;
      archivedAt: string;
      template: string;
      visiblePages: Record<string, boolean>;
    }> & Record<string, unknown>
  ): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);

        // 2. Mesclar com updates
        const updatedData = {
          ...currentData,
          ...updates,
          updatedAt: new Date().toISOString(),
        };

        // 3. Salvar via saveProjectJson (inclui .bak + stampSchema + validateBeforeSave)
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `project.json atualizado (keys: ${Object.keys(updates).join(', ')})`, success: true });
      } catch (error) {
        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `Falha ao atualizar project.json`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Atualiza o onboarding do pesquisador no projeto
   * Salva as respostas do questionário subjetivo no project.json
   */
  async updateProjectOnboarding(
    projectId: string,
    onboardingData: {
      onboarding: {
        knowledgeLevel: 'none' | 'basic' | 'intermediate' | 'advanced';
        researchGoal: 'explore' | 'review' | 'deepen' | 'master';
        fieldExperience: 'none' | 'superficial' | 'moderate' | 'deep';
        availableTime: 'urgent' | 'limited' | 'moderate' | 'ample';
        researchStage: 'starting' | 'exploring' | 'writing' | 'finishing';
      };
      onboardingCompletedAt: string;
    }
  ): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);

        // 2. Mesclar com dados de onboarding
        const updatedData = {
          ...currentData,
          onboarding: onboardingData.onboarding,
          onboardingCompletedAt: onboardingData.onboardingCompletedAt,
          updatedAt: new Date().toISOString(),
        };

        // 3. Salvar via saveProjectJson (inclui .bak + stampSchema + validateBeforeSave)
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: 'Onboarding atualizado', success: true });
      } catch (error) {
        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: 'Falha ao atualizar onboarding', success: false, error: String(error) });
        throw error;
      }
    });
  }

  // ============================================
  // GERENCIAMENTO DE OUTLINE DO PROJETO (FASE 6)
  // ============================================

  /**
   * Atualiza o outline (seções + alocações de blocos) do projeto
   * Usado pelo sistema de Outliner para persistir a estrutura do rascunho
   */
  async updateProjectOutline(
    projectId: string,
    outline: {
      sections: Array<{
        id: string;
        title: string;
        parentId: string | null;
        order: number;
        level: number;
        isCollapsed: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
      placements: Array<{
        id: string;
        blockId: string;
        sectionId: string;
        order: number;
        createdAt: string;
      }>;
      lastModified: string;
    }
  ): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);

        // 2. Mesclar com dados de outline
        const updatedData = {
          ...currentData,
          outline: outline,
          updatedAt: new Date().toISOString(),
        };

        // 3. Salvar via saveProjectJson (inclui .bak + stampSchema + validateBeforeSave)
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: 'Outline atualizado', success: true });
      } catch (error) {
        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: 'Falha ao atualizar outline', success: false, error: String(error) });
        throw error;
      }
    });
  }

  // ============================================
  // GERENCIAMENTO DE ORIENTATION DO PROJETO
  // ============================================

  /**
   * Obtém os dados de orientação (colaboradores, entregas, reuniões) do projeto
   * Retorna null se não existir dados de orientation
   */
  async getProjectOrientation(projectId: string): Promise<OrientationData | null> {
    try {
      const projectData = await this.readProjectJson(projectId);

      // Se não tem dados de orientation, retorna null
      if (!projectData.orientation) {
        return null;
      }

      return projectData.orientation;
    } catch (error) {
      logger.error('GoogleDriveProjects', 'Erro ao obter orientation do projeto:', error);
      return null;
    }
  }

  /**
   * Salva os dados de orientação no projeto
   * Faz merge com dados existentes no project.json
   */
  async saveProjectOrientation(
    projectId: string,
    orientationData: OrientationData
  ): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);

        // 2. Atualizar apenas a seção orientation
        const updatedData = {
          ...currentData,
          orientation: orientationData,
          updatedAt: new Date().toISOString(),
        };

        // 3. Salvar via saveProjectJson (inclui .bak + stampSchema + validateBeforeSave)
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: 'Orientation salva', success: true });
      } catch (error) {
        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: 'Falha ao salvar orientation', success: false, error: String(error) });
        throw error;
      }
    });
  }

  // ============================================
  // GERENCIAMENTO DE REFERENCIAS DO PROJETO
  // ============================================

  /**
   * Obtém todas as referências de um projeto
   */
  async getProjectReferences(projectId: string): Promise<ProjectReference[] | null> {
    try {
      const projectData = await this.readProjectJson(projectId);
      return projectData.references || [];
    } catch (error) {
      logger.error('GoogleDriveProjects', 'Erro ao obter referências do projeto:', error);
      return null;
    }
  }

  /**
   * Adiciona uma referência ao projeto
   */
  async addReferenceToProject(projectId: string, workId: string): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const references: Partial<ProjectReference>[] = currentData.references || [];

        // 2. Verificar se já existe
        if (references.some((ref) => ref.workId === workId)) {
          throw new Error('Referência já está no projeto');
        }

        // 3. Adicionar nova referência
        const newReference = {
          workId,
          archetype: null,
          readStatus: 'unread',
          addedAt: new Date().toISOString()
        };
        references.push(newReference);

        // 4. Atualizar project.json
        const updatedData = {
          ...currentData,
          references,
          updatedAt: new Date().toISOString()
        };

        // 5. Buscar ID do project.json e salvar
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `Referência adicionada: ${workId}`, success: true });
      } catch (error) {
        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `Falha ao adicionar referência: ${workId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Remove uma referência do projeto
   */
  async removeReferenceFromProject(projectId: string, workId: string): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const references: ProjectReference[] = currentData.references || [];

        // 2. Filtrar para remover
        const updatedReferences = references.filter((ref: ProjectReference) => ref.workId !== workId);

        // 3. Atualizar project.json
        const updatedData = {
          ...currentData,
          references: updatedReferences,
          updatedAt: new Date().toISOString()
        };

        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: `Referência removida: ${workId}`, success: true });
      } catch (error) {
        auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: `Falha ao remover referência: ${workId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Atualiza arquétipo e/ou status de leitura de uma referência
   */
  async updateProjectReference(
    projectId: string,
    workId: string,
    updates: Partial<ProjectReference>
  ): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const references: ProjectReference[] = currentData.references || [];

        // 2. Encontrar e atualizar a referência
        const refIndex = references.findIndex((ref: ProjectReference) => ref.workId === workId);
        if (refIndex === -1) {
          throw new Error('Referência não encontrada no projeto');
        }

        references[refIndex] = {
          ...references[refIndex],
          ...updates
        };

        // 3. Atualizar project.json
        const updatedData = {
          ...currentData,
          references,
          updatedAt: new Date().toISOString()
        };

        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: `Referência atualizada: ${workId}`, success: true });
      } catch (error) {
        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: `Falha ao atualizar referência: ${workId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  // ============================================
  // PROJECT RESTORE
  // ============================================

  /**
   * Restaura projeto arquivado ou deletado
   */
  async restoreProject(projectId: string): Promise<void> {
    try {
      const token = await this.getTokenOrRefresh();
      if (!token) {
        throw new Error('Não autenticado. Faça login primeiro.');
      }

      // 1. Buscar informações da pasta
      const getResponse = await driveFetch(
        `https://www.googleapis.com/drive/v3/files/${projectId}?fields=name,trashed`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!getResponse.ok) {
        throw new Error('Erro ao buscar projeto');
      }

      const fileData = await getResponse.json();
      let updates: Partial<{ name: string; trashed: boolean }> = {};

      // 2. Se estava na lixeira, restaurar
      if (fileData.trashed) {
        updates.trashed = false;
      }

      // 3. Se tinha prefixo [ARQUIVADO], remover
      if (fileData.name.startsWith('[ARQUIVADO] ')) {
        updates.name = fileData.name.replace('[ARQUIVADO] ', '');
      }

      // 4. Aplicar mudanças
      if (Object.keys(updates).length > 0) {
        const updateResponse = await driveFetch(
          `https://www.googleapis.com/drive/v3/files/${projectId}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updates),
          }
        );

        if (!updateResponse.ok) {
          const error = await updateResponse.json();
          throw new Error(`Erro ao restaurar projeto: ${error.error.message}`);
        }
      }

      auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: 'Projeto restaurado', success: true });
    } catch (error) {
      auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: 'Falha ao restaurar projeto', success: false, error: String(error) });
      throw error;
    }
  }

  // ============================================
  // PERMANENT DELETE
  // ============================================

  /**
   * Deleta projeto permanentemente (remove da lixeira)
   */
  async deletePermanently(projectId: string): Promise<void> {
    try {
      const token = await this.getTokenOrRefresh();
      if (!token) {
        throw new Error('Não autenticado. Faça login primeiro.');
      }

      const response = await driveFetch(
        `https://www.googleapis.com/drive/v3/files/${projectId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Erro ao deletar permanentemente: ${error.error.message}`);
      }

      auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: 'Projeto deletado permanentemente', success: true });
    } catch (error) {
      auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: 'Falha ao deletar projeto permanentemente', success: false, error: String(error) });
      throw error;
    }
  }

  // ============================================
  // DADOS DE PESQUISA DO PROJETO
  // ============================================

  /**
   * Adiciona um item do repositório aos dados de pesquisa do projeto
   */
  async addResearchDataToProject(projectId: string, repositoryItemId: string): Promise<string> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const researchData: ProjectResearchData[] = currentData.researchData || [];

        // 2. Verificar se já existe
        if (researchData.find((d: ProjectResearchData) => d.repositoryItemId === repositoryItemId)) {
          throw new Error('Este dado já foi adicionado ao projeto');
        }

        // 3. Criar novo registro
        const newDataId = `rd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newResearchData = {
          id: newDataId,
          repositoryItemId,
          addedAt: new Date().toISOString(),
          relevance: null,
          integrationTags: [],
          notes: '',
          notesCount: 0,
          isFavorite: false
        };

        researchData.push(newResearchData);

        // 4. Salvar project.json atualizado
        const updatedData = {
          ...currentData,
          researchData,
          updatedAt: new Date().toISOString()
        };

        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `Dado de pesquisa adicionado: ${newDataId}`, success: true });
        return newDataId;
      } catch (error) {
        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `Falha ao adicionar dado de pesquisa: ${repositoryItemId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Retorna os dados de pesquisa do projeto
   */
  async getProjectResearchData(projectId: string): Promise<ProjectResearchData[]> {
    const token = await this.getTokenOrRefresh();
    if (!token) {
      throw new Error('Não autenticado. Faça login primeiro.');
    }

    const currentData = await this.readProjectJson(projectId);
    return currentData.researchData || [];
  }

  /**
   * Remove um dado de pesquisa do projeto
   */
  async removeResearchDataFromProject(projectId: string, dataId: string): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const researchData: ProjectResearchData[] = currentData.researchData || [];

        // 2. Filtrar para remover o item
        const updatedResearchData = researchData.filter((d: ProjectResearchData) => d.id !== dataId);

        // 3. Salvar project.json atualizado
        const updatedData = {
          ...currentData,
          researchData: updatedResearchData,
          updatedAt: new Date().toISOString()
        };

        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: `Dado de pesquisa removido: ${dataId}`, success: true });
      } catch (error) {
        auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: `Falha ao remover dado de pesquisa: ${dataId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Atualiza um dado de pesquisa no projeto
   */
  async updateProjectResearchData(
    projectId: string,
    dataId: string,
    updates: Partial<ProjectResearchData>
  ): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const researchData: (ProjectResearchData & Record<string, unknown>)[] = currentData.researchData || [];

        // 2. Encontrar e atualizar o dado
        const dataIndex = researchData.findIndex((d) => d.id === dataId);
        if (dataIndex === -1) {
          throw new Error('Dado de pesquisa não encontrado no projeto');
        }

        researchData[dataIndex] = {
          ...researchData[dataIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };

        // 3. Salvar project.json atualizado
        const updatedData = {
          ...currentData,
          researchData,
          updatedAt: new Date().toISOString()
        };

        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: `Dado de pesquisa atualizado: ${dataId}`, success: true });
      } catch (error) {
        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: `Falha ao atualizar dado de pesquisa: ${dataId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  // ============================================
  // CRUZAMENTOS (CROSSINGS)
  // ============================================

  /**
   * Obtém os dados de cruzamentos do projeto
   */
  async getProjectCrossings(projectId: string): Promise<ProjectCrossing[]> {
    const token = await this.getTokenOrRefresh();
    if (!token) {
      throw new Error('Não autenticado. Faça login primeiro.');
    }

    const currentData = await this.readProjectJson(projectId);
    return currentData.crossings || [];
  }

  /**
   * Atualiza um cruzamento no projeto (cria se não existir)
   */
  async updateProjectCrossing(
    projectId: string,
    tagName: string,
    updates: Partial<{
      customTitle: string;
      comments: string;
    }>
  ): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const crossings: Array<Record<string, unknown>> = currentData.crossings || [];

        // 2. Encontrar ou criar o cruzamento
        const crossingIndex = crossings.findIndex((c: Record<string, unknown>) => c.tagName === tagName);

        if (crossingIndex >= 0) {
          // Atualiza existente
          crossings[crossingIndex] = {
            ...crossings[crossingIndex],
            ...updates,
            updatedAt: new Date().toISOString()
          };
        } else {
          // Cria novo
          crossings.push({
            tagName,
            ...updates,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }

        // 3. Salvar project.json atualizado
        const updatedData = {
          ...currentData,
          crossings,
          updatedAt: new Date().toISOString()
        };

        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `Cruzamento atualizado: ${tagName}`, success: true });
      } catch (error) {
        auditLog({ operation: 'save', target: 'projeto', targetId: projectId, detail: `Falha ao atualizar cruzamento: ${tagName}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  // ============================================
  // GERENCIAMENTO DE GRUPOS DE DADOS (FASE 3)
  // ============================================

  /**
   * Obtém todos os grupos de um projeto
   */
  async getProjectGroups(projectId: string): Promise<DataGroup[]> {
    try {
      const projectData = await this.readProjectJson(projectId);
      return projectData.groups || [];
    } catch (error) {
      logger.error('GoogleDriveProjects', 'Erro ao obter grupos do projeto:', error);
      return [];
    }
  }

  /**
   * Adiciona um grupo ao projeto
   */
  async addGroupToProject(projectId: string, name: string, color?: string): Promise<string> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const groups: DataGroup[] = currentData.groups || [];

        // 2. Verificar se já existe grupo com mesmo nome
        if (groups.find((g: DataGroup) => g.name.toLowerCase() === name.toLowerCase())) {
          throw new Error('Já existe um grupo com este nome');
        }

        // 3. Criar novo grupo
        const newGroup = {
          id: `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name,
          color: color || '#477B72',
          order: groups.length,
          createdAt: new Date().toISOString()
        };

        // 4. Adicionar ao array
        groups.push(newGroup);

        // 5. Salvar
        const updatedData = {
          ...currentData,
          groups,
          updatedAt: new Date().toISOString()
        };
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'create', target: 'projeto', targetId: projectId, detail: `Grupo criado: ${name} (${newGroup.id})`, success: true });
        return newGroup.id;
      } catch (error) {
        auditLog({ operation: 'create', target: 'projeto', targetId: projectId, detail: `Falha ao criar grupo: ${name}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Atualiza um grupo do projeto
   */
  async updateProjectGroup(projectId: string, groupId: string, updates: {
    name?: string;
    color?: string;
    order?: number;
  }): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const groups: (DataGroup & Record<string, unknown>)[] = currentData.groups || [];

        // 2. Encontrar o grupo
        const groupIndex = groups.findIndex((g) => g.id === groupId);
        if (groupIndex === -1) {
          throw new Error('Grupo não encontrado');
        }

        // 3. Verificar nome duplicado (se estiver atualizando nome)
        if (updates.name) {
          const duplicate = groups.find((g: DataGroup) =>
            g.id !== groupId && g.name.toLowerCase() === updates.name!.toLowerCase()
          );
          if (duplicate) {
            throw new Error('Já existe um grupo com este nome');
          }
        }

        // 4. Atualizar
        groups[groupIndex] = {
          ...groups[groupIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };

        // 5. Salvar
        const updatedData = {
          ...currentData,
          groups,
          updatedAt: new Date().toISOString()
        };
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: `Grupo atualizado: ${groupId}`, success: true });
      } catch (error) {
        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: `Falha ao atualizar grupo: ${groupId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Remove um grupo do projeto
   * Dados associados ficam sem grupo (groupId = null)
   */
  async deleteProjectGroup(projectId: string, groupId: string): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const groups: DataGroup[] = currentData.groups || [];
        const researchData: ProjectResearchData[] = currentData.researchData || [];

        // 2. Remover grupo
        const updatedGroups = groups.filter((g: DataGroup) => g.id !== groupId);

        // 3. Limpar groupId dos dados associados
        const updatedResearchData = researchData.map((d: ProjectResearchData) =>
          d.groupId === groupId ? { ...d, groupId: null } : d
        );

        // 4. Reordenar grupos restantes
        updatedGroups.forEach((g: DataGroup, index: number) => {
          g.order = index;
        });

        // 5. Salvar
        const updatedData = {
          ...currentData,
          groups: updatedGroups,
          researchData: updatedResearchData,
          updatedAt: new Date().toISOString()
        };
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: `Grupo removido: ${groupId}`, success: true });
      } catch (error) {
        auditLog({ operation: 'delete', target: 'projeto', targetId: projectId, detail: `Falha ao remover grupo: ${groupId}`, success: false, error: String(error) });
        throw error;
      }
    });
  }

  /**
   * Reordena grupos do projeto
   */
  async reorderProjectGroups(projectId: string, groupIds: string[]): Promise<void> {
    return this.withProjectLock(projectId, async () => {
      try {
        const token = await this.getTokenOrRefresh();
        if (!token) {
          throw new Error('Não autenticado. Faça login primeiro.');
        }

        // 1. Ler project.json atual
        const currentData = await this.readProjectJson(projectId);
        const groups: DataGroup[] = currentData.groups || [];

        // 2. Reordenar baseado na nova ordem
        const reorderedGroups = groupIds.map((id, index) => {
          const group = groups.find((g: DataGroup) => g.id === id);
          if (group) {
            return { ...group, order: index };
          }
          return null;
        }).filter(Boolean);

        // 3. Salvar
        const updatedData = {
          ...currentData,
          groups: reorderedGroups,
          updatedAt: new Date().toISOString()
        };
        await this.saveProjectJson(projectId, updatedData);

        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: 'Grupos reordenados', success: true });
      } catch (error) {
        auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: 'Falha ao reordenar grupos', success: false, error: String(error) });
        throw error;
      }
    });
  }

  // ============================================
  // FASE EMPÍRICA v4 — MIGRATION ORQUESTRADA v5→v6 (DN8/DN9)
  // ============================================

  /**
   * Auto-popula `empiricalCodebook.categories` em project.json a partir de
   * labels antigos varridos dos SavedPdfData de cada highlight do projeto.
   *
   * Pilar 1: preserva o trabalho do beta tester. Pesquisadores que codificaram
   * highlights pré-v4 com labels-string (Adrian, Victor, Andreu, Gabriel, Fábio)
   * têm esses labels coletados, transformados em categorias com IDs estáveis +
   * cores DN30, e persistidos no project.json.
   *
   * Idempotência (DN13 + Pilar 1):
   * - Rodar 2× com os mesmos highlights produz o mesmo codebook
   * - Categorias já existentes (por label) são preservadas com seus IDs
   * - Categorias novas (label não conhecido) são acrescidas no fim
   *
   * **Não wired automaticamente em load path no Sprint 0.1** — esta função é
   * disponibilizada para chamada explícita por sprints futuros (0.2/0.3/0.4)
   * ou backfill script. O framework `schemaMigration.ts` v5→v6 já inicializa
   * codebook vazio sincronicamente; este método enriquece o codebook async-
   * mente quando os highlights estiverem acessíveis. **NÃO bloqueia load se
   * algum highlight falha** — Pilar 1 (continuidade preservada).
   *
   * @param projectId ID do project folder no Google Drive
   * @param workIds IDs dos PDFs/SavedPdfData da Mesa
   * @param loadHighlights callback que abstrai dependência de `loadHighlights`
   *   da subclasse Repository (permite uso em testes via mock)
   * @returns codebook atualizado, ou null se falha total na leitura
   */
  async migrateProjectV5toV6Codebook(
    projectId: string,
    workIds: ReadonlyArray<string>,
    loadHighlights: LoadHighlightsCallback,
  ): Promise<EmpiricalCodebook | null> {
    return this.withProjectLock(projectId, async () => {
      try {
        // 1. Ler project.json (já passou pela migration sync v5→v6 do framework
        //    se vinha de v5 — empiricalCodebook está pelo menos vazio)
        const currentData = await this.readProjectJson(projectId);
        const codebook: EmpiricalCodebook = currentData.empiricalCodebook ?? {
          columns: [],
          categories: [],
        };

        // 2. Idempotência: se já tem categorias, não re-popular automaticamente
        //    (caller pode forçar passando workIds + checagem externa)
        if (codebook.categories.length > 0) {
          auditLog({
            operation: 'update',
            target: 'projeto',
            targetId: projectId,
            detail: 'migrateProjectV5toV6Codebook: codebook já populado, skip',
            success: true,
          });
          return codebook;
        }

        // 3. Coletar labels de cada SavedPdfData (resilient — não bloquear se
        //    algum highlights file falha; Pilar 1)
        const allLabels = new Set<string>();
        let failures = 0;
        for (const workId of workIds) {
          try {
            const pdfData = await loadHighlights(workId);
            const labels = collectLabelsFromHighlights(pdfData?.highlights ?? []);
            for (const l of labels) allLabels.add(l);
          } catch (err) {
            failures += 1;
            logger.warn('GoogleDriveProjects', '[migrateProjectV5toV6Codebook] falha ao ler highlights', { workId, error: String(err) });
          }
        }

        // 4. Funde no codebook (idempotente — sem labels: codebook fica vazio)
        const updatedCodebook = mergeCodebookWithLabels(codebook, Array.from(allLabels));

        // 5. Salvar project.json apenas se houve mudança real (não force-save
        //    em codebook já vazio — respeita Padrão 2: backup .bak na primeira
        //    mutação legítima da sessão, não na migration in-place)
        if (updatedCodebook.categories.length > codebook.categories.length) {
          const updatedData = {
            ...currentData,
            empiricalCodebook: updatedCodebook,
            updatedAt: new Date().toISOString(),
          };
          await this.saveProjectJson(projectId, updatedData);
        }

        auditLog({
          operation: 'update',
          target: 'projeto',
          targetId: projectId,
          detail: `migrateProjectV5toV6Codebook: ${updatedCodebook.categories.length} categorias (${updatedCodebook.categories.length - codebook.categories.length} novas, ${failures} falhas de leitura)`,
          success: true,
        });

        return updatedCodebook;
      } catch (error) {
        auditLog({
          operation: 'update',
          target: 'projeto',
          targetId: projectId,
          detail: 'migrateProjectV5toV6Codebook: falha total',
          success: false,
          error: String(error),
        });
        logger.error('GoogleDriveProjects', '[migrateProjectV5toV6Codebook] falha total', error);
        return null;
      }
    });
  }

}
